import React, { createContext, useContext } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Configuration } from '@digiwedge/access-control-api-client';
const client = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                const status = error?.response?.status;
                if (status && status >= 400 && status < 500)
                    return false;
                return failureCount < 3;
            },
            retryDelay: (attemptIndex) => Math.min(30_000, 1000 * 2 ** attemptIndex),
        },
    },
});
const AccessControlContext = createContext({});
export function useAccessControlContext() {
    return useContext(AccessControlContext);
}
export function normalizeAccessControlBasePath(baseUrl) {
    const trimmed = baseUrl?.trim().replace(/\/+$/, '') ?? '';
    if (!trimmed || trimmed === '/api')
        return '';
    return trimmed.endsWith('/api') ? trimmed.slice(0, -4) : trimmed;
}
export function createAccessControlQueryScope({ authMode = 'bearer', baseUrl, tenantId, }) {
    return {
        authMode,
        basePath: normalizeAccessControlBasePath(baseUrl),
        tenantId: tenantId ?? null,
    };
}
export function createAccessControlConfiguration(context) {
    const authMode = context.authMode ?? 'bearer';
    const baseUrl = context.baseUrl;
    const credentials = context.authMode === 'cookie' ? (context.credentials ?? 'include') : 'include';
    const getAccessToken = 'getAccessToken' in context ? context.getAccessToken : undefined;
    const tenantId = context.tenantId;
    const headers = tenantId ? { 'x-tenant-id': tenantId } : {};
    return new Configuration({
        basePath: normalizeAccessControlBasePath(baseUrl),
        ...(authMode === 'bearer' && getAccessToken
            ? {
                accessToken: () => {
                    const token = getAccessToken();
                    if (!token)
                        throw new Error('Access Control API token unavailable');
                    return token;
                },
            }
            : {}),
        baseOptions: {
            ...(authMode === 'cookie'
                ? { withCredentials: credentials === 'include', withXSRFToken: credentials === 'include' }
                : {}),
            headers,
        },
    });
}
export function AccessControlApiProvider(props) {
    const { authMode, children, baseUrl, tenantId } = props;
    const contextValue = authMode === 'cookie'
        ? { authMode, baseUrl, credentials: props.credentials, tenantId }
        : { authMode, baseUrl, getAccessToken: props.getAccessToken, tenantId };
    return (React.createElement(QueryClientProvider, { client: client },
        React.createElement(AccessControlContext.Provider, { value: contextValue }, children)));
}
//# sourceMappingURL=AccessControlApiProvider.js.map